import { Component, ViewChild, ElementRef, AfterViewInit, OnInit, OnDestroy } from '@angular/core';
import { LlPermissionsService } from '../ll-permissions.service';
import { LlModelsService } from '../ll-models.service';

interface EligibleChoice {
  code: string;
  LOGICRESULTINOROUT: string;
  SELECTED: boolean;
}

interface Bundle {
  showRemaining: boolean;
  function: string;
  parentW: number;
  parentH: number;
  remainingX: number;
  remainingY: number;
  remainingW: number;
  remainingH: number;
  tableW: number;
  transaction: any;
  rules: any[];
  blankRowCount: number;
  SENDERNAME: string;
  LOGICRESULTINOROUT: string;
  CASHLOGICSTATUS: string;
  eligibleChoices: EligibleChoice[];
  isDirty: boolean;
}

@Component({
  selector: 'll-model-cash-rule-edit',
  templateUrl: './ll-model-cash-rule-edit.component.html',
  styleUrls: ['./ll-model-cash-rule-edit.component.css']
})
export class LlModelCashRuleEditComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mainDiv_ll_model_cash_rule_edit') mainDiv!: ElementRef;

  bundle: Bundle = {
    showRemaining: false,
    function: '',
    parentW: 0,
    parentH: 0,
    remainingX: 0,
    remainingY: 20,
    remainingW: 0,
    remainingH: 0,
    tableW: 0,
    transaction: {},
    rules: [],
    blankRowCount: 5,
    SENDERNAME: '',
    LOGICRESULTINOROUT: '',
    CASHLOGICSTATUS: '',
    eligibleChoices: [],
    isDirty: false
  };

  // New: Message to show next to Save button
  saveMessage = '';
  saveMessageType: 'error' | 'success' | '' = '';

  private eventSubscription_ll_model_cash_switch?: any;

  constructor(
    private LlPermissionsService: LlPermissionsService,
    private LlModelsService: LlModelsService,
  ) { }

  ngOnInit(): void { }

  ngOnDestroy(): void {
    if (this.eventSubscription_ll_model_cash_switch) {
      this.eventSubscription_ll_model_cash_switch.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    console.log("ngAfterViewInit()");
    this.setVars();
  }

  getDummyArray(count: number): number[] {
    return Array.from({ length: count }, (_, i) => i);
  }

  onRuleChange(): void {
    this.bundle.isDirty = true;
    this.clearSaveMessage();           // Clear message when user starts editing
  }

  saveRules(): void {
    // 1. Check for partial rule rows
    const hasPartialRule = this.bundle.rules.some(rule => {
      const col = rule.TARGETCOLUMN ? rule.TARGETCOLUMN.toString().trim() : '';
      const qual = rule.QUALIFIER ? rule.QUALIFIER.toString().trim() : '';
      const val = rule.TARGETVALUE ? rule.TARGETVALUE.toString().trim() : '';

      const filledCount = (col !== '' ? 1 : 0) + (qual !== '' ? 1 : 0) + (val !== '' ? 1 : 0);

      return filledCount > 0 && filledCount < 3;
    });

    // 2. Check at least one complete rule row
    const hasAtLeastOneCompleteRow = this.bundle.rules.some(rule => {
      const col = rule.TARGETCOLUMN ? rule.TARGETCOLUMN.toString().trim() : '';
      const qual = rule.QUALIFIER ? rule.QUALIFIER.toString().trim() : '';
      const val = rule.TARGETVALUE ? rule.TARGETVALUE.toString().trim() : '';

      return col !== '' && qual !== '' && val !== '';
    });

    

    // 3. NEW: Rule In/Out must be selected
    const inOutSelected = this.bundle.rules[0].LOGICRESULTINOROUT.toLowerCase() === 'in' ||
      this.bundle.rules[0].LOGICRESULTINOROUT.toLowerCase() === 'out';

    if (hasPartialRule) {
      this.saveMessage = "Partial rules are not allowed. Each row must be either completely empty or fully filled.";
      this.saveMessageType = 'error';
      return;
    }

    if (!hasAtLeastOneCompleteRow) {
      this.saveMessage = "At least one complete rule is required.";
      this.saveMessageType = 'error';
      return;
    }

    if (!inOutSelected) {
      this.saveMessage = "Rule In/Out selection is required.";
      this.saveMessageType = 'error';
      return;
    }

    // All checks passed
    this.bundle.isDirty = false;
    this.LlModelsService.NG_LL_model_AR_cash_targetRule_edit(this.bundle.rules);

    this.saveMessage = "Rules saved successfully.";
    this.saveMessageType = 'success';

    setTimeout(() => this.clearSaveMessage(), 3000);
  }


  clearSaveMessage(): void {
    this.saveMessage = '';
    this.saveMessageType = '';
  }

  deleteRules(): void {
    this.bundle.isDirty = false;
    this.clearSaveMessage();
  }

  selectAgeCap(event: any, param: string): void {
    this.bundle.LOGICRESULTINOROUT = event.target.value;
    if (this.bundle.rules.length > 0) {
      this.bundle.rules[0].LOGICRESULTINOROUT = event.target.value;
    }
    this.onRuleChange();
  }

  private setVars(): void {
    if (!this.mainDiv?.nativeElement?.parentElement) {
      return;
    }
    this.bundle.function = this.mainDiv.nativeElement.parentElement.id;
    this.bundle.parentW = this.mainDiv.nativeElement.parentElement.parentElement.clientWidth;
    this.bundle.parentH = this.mainDiv.nativeElement.parentElement.parentElement.clientHeight;

    setTimeout(() => {
      this.bundle.remainingW = this.bundle.parentW;
      this.bundle.tableW = this.bundle.remainingW - 20;
      this.bundle.remainingH = this.bundle.parentH - this.bundle.remainingY;
      this.getDate();
    }, 0);
  }

  private getDate(): void {
    this.LlModelsService.NG_LL_model_AR_cash_targetRule_get((arr: any[]) => {
      if (arr && arr.length > 0) {
        this.bundle.rules = arr;
        this.bundle.blankRowCount = 5 - arr.length;

        const firstRule = this.bundle.rules[0];
        this.bundle.SENDERNAME = firstRule.COMPANYABREV;

        // Removed default to 'In' - now uses whatever comes from the backend
        this.bundle.LOGICRESULTINOROUT = firstRule.LOGICRESULTINOROUT || '';

        this.bundle.eligibleChoices = [
          {
            code: 'IN',
            LOGICRESULTINOROUT: 'In',
            SELECTED: this.bundle.LOGICRESULTINOROUT.toLowerCase() === 'in'
          },
          {
            code: 'OUT',
            LOGICRESULTINOROUT: 'Out',
            SELECTED: this.bundle.LOGICRESULTINOROUT.toLowerCase() === 'out'
          }
        ];

        if (firstRule.CASHLOGICID === 0) {
          this.LlModelsService.getActiveCashTransaction((obj: any) => {
            this.bundle.transaction = obj;
            if (this.bundle.transaction.SENDERNAME) {
              firstRule.COMPANYABREV = this.bundle.transaction.SENDERNAME;
              firstRule.SENDERCOMPANYID = this.bundle.transaction.SENDERCOMPANYID;
              this.bundle.SENDERNAME = this.bundle.transaction.SENDERNAME;
            }
          });
        } else {
          this.bundle.transaction = {};
        }

        this.bundle.isDirty = false;
        this.clearSaveMessage();
      }
    });
  }
}
